<template>
  <div>
    <import-dialog
      title="Import CashFlow"
      :message="importMessage"
      :validate="true"
      :uploadUrl="importUrl"
      :visible="showFileUploadDialog"
      v-if="showFileUploadDialog"
      @close="hideImportDialog"
      @onSuccess="onSuccess"
    ></import-dialog>

    <page-toolbar>
      <div style="padding-right:8px">
        <kendo-button icon="check" title="Save changes" @click="saveChanges" v-if="!isReadOnly">
        </kendo-button>
        <kendo-button
          icon="cancel"
          title="Cancel changes"
          @click="cancelChanges"
          v-if="!isReadOnly"
        >
        </kendo-button>
      </div>
      <flow-type-toolbar
        @change="toolbarChange"
        :numberOfPeriods="numberOfPeriods"
        :is-read-only="isReadOnly"
        :market-id="marketId"
        source="DivisionForecast"
      >
      </flow-type-toolbar>
      <kendo-button
        icon="redo"
        title="Generate Futures Forecast"
        @click="generateFutureForecast"
        v-if="showFutureForecast"
      >
      </kendo-button>
    </page-toolbar>
    <div ref="forecastGrid"></div>
  </div>
</template>
<script>
import {mapGetters} from 'vuex'
import FlowTypeToolbar from '@/components/FlowTypeToolbarWithSecondUnit'
import notification from '@/helpers/notification'
import axios from 'axios'
import {numericEditor} from '@/helpers/kendo-editors'
import {enableCustomExportExcel} from '@/helpers/export-helper'
import ImportDialog from '../../components/ImportDialog'
import PageToolbar from '@/components/PageToolbar'
import security from '@/mixins/security'
import confirmationWindow from '@/helpers/confirmation-window'

const $ = kendo.jQuery

export default {
  mixins: [security],
  computed: {
    ...mapGetters({marketId: 'app/marketId'}),
    ...mapGetters({clientId: 'authentication/selectedClientId'}),
    showFutureForecast() {
      return (
        this.lastOptions.marketId == 1 &&
        !this.isReadOnly &&
        this.lastOptions.typeNumId == 1 &&
        this.hasFutureContract
      )
    },
    exposureTypeNumWatch() {
      return this.lastOptions.typeNumId, this.lastOptions.exposureCcy
    },
  },
  components: {
    PageToolbar,
    FlowTypeToolbar,
    ImportDialog,
  },
  watch: {
    async exposureTypeNumWatch() {
      const {data} = await axios.get('/api/list/DivisionForecast/hasFutureContract', {
        params: {clientId: this.clientId, ccy: this.lastOptions.exposureCcy},
      })
      this.hasFutureContract = data
    },
  },
  data() {
    return {
      hasFutureContract: false,
      showFileUploadDialog: false,
      numberOfPeriods: 12,
      lastOptions: {},
      importUrl: '',
      importMessage: '',
    }
  },
  beforeDestroy() {
    const $container = $(this.$refs.forecastGrid)
    let forecastGrid = $container.getKendoGrid()
    if (forecastGrid != null) {
      forecastGrid.destroy()
    }
  },
  methods: {
    refreshGrid() {
      if (this.lastOptions) {
        this.createGrid(this.lastOptions)
      }
    },
    hideImportDialog() {
      this.showFileUploadDialog = false
      this.importMessage = ''
    },
    onSuccess(e) {
      if (e.status == 0) {
        notification(e.message, 'info')
        this.refreshGrid()
        this.hideImportDialog()
      } else {
        notification(e.message, 'error')
        this.importMessage = e.message
      }
    },
    toolbarChange(e) {
      this.lastOptions = e
      this.importUrl = `/api/list/DivisionForecast/importCashFlow?flowTypeId=${
        this.lastOptions.flowTypeId
      }&ccy=${this.lastOptions.exposureCcy}&marketId=${this.lastOptions.marketId}&unitId=${
        this.lastOptions.unitId
      }&forecastDate=${this.lastOptions.date}`
      this.createGrid(e)
    },
    getGridColumns(columns) {
      let allColumns = [
        {
          field: 'ForecastDate',
          title: 'ForecastDate',
          hidden: true,
        },
        {
          field: 'Ccy',
          title: 'Ccy',
          hidden: true,
        },
        {
          field: 'FlowTypeId',
          title: 'FlowTypeId',
          hidden: true,
        },
        {
          field: 'PeriodMonth',
          title: 'Month',
          lockable: false,
          editable: false,
          width: 105,
          sortable: true,
          attributes: {Class: 'EvaluationDate'},
        },
      ]

      columns
        .filter(function(column) {
          return column.division !== 'TOTAL'
        })
        .forEach(function(column) {
          allColumns.push({
            field: column.columnName,
            title: column.title,
            width: 150,
            headerAttributes: {style: 'text-align: center; font-size: 12px;'},
            attributes: {style: 'text-align: right;'},
            sortable: false,
            template: function(e) {
              let value = e[column.columnName] || 0
              return "<span style='cursor:cell;'>" + kendo.toString(value, 'n0') + '</span>'
            },
            editor: numericEditor(
              {
                format: 'n0',
                spinners: false,
                decimals: 16,
                step: 0,
              },
              false,
              column.division === 'TOTAL',
            ),
          })
        })

      allColumns.push({
        title: 'TOTAL',
        width: 150,
        headerAttributes: {style: 'text-align: center; font-size: 12px;'},
        attributes: {style: 'text-align: right;'},
        template: function(dataItem) {
          let sum = Object.keys(dataItem)
            .filter(function(prop) {
              return /Col\d+/.test(prop) && prop !== 'Col0'
            })
            .map(function(prop) {
              return dataItem[prop]
            })
            .reduce(function(a, b) {
              return a + b
            }, 0)

          return kendo.toString(sum, 'N0')
        },
      })

      allColumns.push({
        width: '60px',
        attributes: {style: 'text-align: center;'},
        template: dataItem => {
          if (!dataItem.CanBeDeleted || this.isReadOnly) {
            return ''
          }
          const template =
            '<a class="deleteCashflow" title="Delete" data-period="' +
            dataItem.Period +
            '" href="javascript:;"> <span class="k-icon k-i-delete"></span></a>'
          return template
        },
      })

      allColumns.push({})

      return allColumns
    },
    getDynamicFields(columns, editable) {
      let fields = {
        period: {type: 'number', defaultValue: 0, editable: false},
        ccy: {type: 'string', editable: false},
        flowTypeId: {type: 'number', editable: false},
        forecastDate: {type: 'date', editable: false},
        periodMonth: {editable: false, type: 'string'},
      }

      columns.forEach(function(column) {
        fields[column.columnName] = {
          field: column.columnName,
          editable: column.division !== 'TOTAL' && editable,
          defaultValue: 0,
          validation: {required: true},
        }
      })

      fields['CanBeDeleted'] = {type: 'boolean', defaultValue: true, editable: false}
      fields['PeriodMonth'] = {type: 'string', editable: false}

      return fields
    },
    getDataItem(data, columns) {
      let validColumns = columns
      let period = data['Period']
      let ccy = data['Ccy']
      let flowTypeId = data['FlowTypeId']
      let forecastDate = kendo.toString(data['ForecastDate'], 'yyyy-MM-dd')
      let forecastItems = []

      validColumns.forEach(function(column) {
        const value = data[column.columnName] || 0
        const newDataItem = {
          divisionId: column.divisionId,
          Value: value,
        }
        forecastItems.push(newDataItem)
      })

      let dataItem = {
        period: period,
        ccy: ccy,
        flowTypeId: flowTypeId,
        forecastDate: forecastDate,
        forecastItems: forecastItems,
      }

      return dataItem
    },
    async addRow() {},
    saveChanges() {
      let $container = $(this.$refs.forecastGrid)
      let forecastGrid = $container.getKendoGrid()
      forecastGrid.saveChanges()
    },
    cancelChanges() {
      let $container = $(this.$refs.forecastGrid)
      let forecastGrid = $container.getKendoGrid()
      forecastGrid.cancelChanges()
    },
    createGrid(options) {
      const vm = this
      let $container = $(vm.$refs.forecastGrid)
      let editable = !vm.isReadOnly
      let showExcelButton = true
      const url = '/api/list/DivisionForecast'

      return new Promise(function(gridResolve) {
        axios
          .get(url, {
            params: {
              clientId: options.clientId,
              ccyPairId: options.ccyPairId,
              exposureCcy: options.exposureCcy,
              flowTypeId: options.flowTypeId,
              forecastDate: options.date,
              ratio: options.ratio,
            },
          })
          .then(function(result) {
            let response = result.data
            let columns = response.columns

            let dataSource = new kendo.data.DataSource({
              transport: {
                read: function(options) {
                  options.success(response)
                },
                update: function(e) {
                  let data = e.data.models.map(function(data) {
                    return vm.getDataItem(data, columns)
                  })
                  axios
                    .put(url, {
                      clientId: options.clientId,
                      dataItems: data,
                    })
                    .then(function() {
                      e.success(true)
                    })
                },
              },
              schema: {
                data: 'data',
                errors: 'errors',
                model: {
                  id: 'period',
                  fields: vm.getDynamicFields(columns, editable),
                },
              },
              batch: true,
            })

            let forecastGrid = $container.getKendoGrid()
            if (forecastGrid != null) {
              forecastGrid.destroy()
            }

            forecastGrid = $container
              .empty()
              .kendoGrid({
                navigatable: true,
                dataSource: dataSource,
                editable: editable && !response.ReadOnly && 'incell',
                hidden: false,
                resizable: false,
                reorderable: false,
                sortable: true,
                scrollable: true,
                height: 'calc(100vh - 170px)',
                columns: vm.getGridColumns(columns),
              })
              .data('kendoGrid')

            forecastGrid.trigger('dataBound')
            gridResolve(forecastGrid)
            vm.numberOfPeriods = response.data.length

            let gridToolbar = $('.page_toolbar-item')

            if (editable) {
              $('.page_toolbar-item .AddNew').remove()
              const validDate = new Date(options.date).getYear() > 0
              if (validDate) {
                let $button = $(
                  "<a class='AddNew k-button k-button-icontext' href='javascript:;' title='Add New Record'><i class='k-icon k-i-plus'></i></a>",
                )
                $button.on('click', function() {
                  axios
                    .post(url, {
                      clientId: options.clientId,
                      ccyPairId: options.ccyPairId,
                      exposureCcy: options.exposureCcy,
                      flowTypeId: options.flowTypeId,
                      forecastDate: options.date,
                    })
                    .then(function(result) {
                      var response = result.data
                      if (response.errors) {
                        let message = response.Errors.Item.errors
                        notification(message, 'error')
                      } else {
                        vm.createGrid(options)
                      }
                    })
                })
                gridToolbar.append($button)
              }

              let $importButton = $(
                "<a class='ImportData k-button' href='javascript:;' title='Import Cash Flow'> <i aria-hidden='true' class='v-icon material-icons theme--light' style='font-size: 16px;text-align: center;'>archive</i> </i></a>",
              )
              $importButton.on('click', function() {
                vm.importMessage = ''
                vm.showFileUploadDialog = true
                vm.createGrid(options)
              })
              $('.page_toolbar-item .ImportData').remove()
              gridToolbar.append($importButton)

              $container.off('click', '.deleteCashflow').on('click', '.deleteCashflow', function() {
                var period = $(this).data('period')
                axios
                  .delete(url, {
                    params: {
                      clientId: options.clientId,
                      ccyPairId: options.ccyPairId,
                      exposureCcy: options.exposureCcy,
                      flowTypeId: options.flowTypeId,
                      forecastDate: options.date,
                      period: period,
                    },
                  })
                  .then(function() {
                    vm.createGrid(options)
                  })
              })
            }

            if (showExcelButton) {
              $.ajaxSettings.traditional = true
              let params = $.param({
                clientId: options.clientId,
                ccyPairId: options.ccyPairId,
                exposureCcy: options.exposureCcy,
                flowTypeId: options.flowTypeId,
                forecastDate: options.date,
                ratio: options.ratio,
                unitId: options.unitId,
                marketId: options.marketId,
              })
              delete $.ajaxSettings.traditional

              $('.page_toolbar-item .exportToExcel').remove()
              let excelUrl = url + '/exportCashFlowDivisionDetailToExcel' + '?' + params
              enableCustomExportExcel(gridToolbar, excelUrl)
            }
          })
      })
    },
    async generateFutureForecast() {
      confirmationWindow.openConfirmationWindow(
        'Generate Futures Forecast',
        'Are you sure you want to generate the Futures forecast?',
        async () => {
          await axios.post(
            `/api/list/DivisionForecast/generateFutureForecast?flowTypeId=${
              this.lastOptions.flowTypeId
            }&ccy=${this.lastOptions.exposureCcy}&forecastDate=${this.lastOptions.date}`,
          )
          this.refreshGrid()
        },
      )
    },
  },
}
</script>
